import { mapValues, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ProductionComponentInstance } from '@paragon/contract-library';
import { createMappingByKey } from '@paragon/production-app-utilities';
import { sortBy } from '@paragon/utilities';

import { WidthCache } from '../ComponentGeometryCaches';
import { RootState } from '../store';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab } from './getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab';
import { getLoadedTrussesByAnalysisSetGuid } from './trusses';

export const getBatchesByProductionGroupGuid = createSelector(
  (state: RootState) => state.batches,
  (state: RootState) => state.productionGroups,

  (batches, productionGroups) => createMappingByKey(batches, (batch) => batch.productionGroupGuid, productionGroups),
);

export const getBatchesForActiveProductionGroupGuid = createSelector(
  getBatchesByProductionGroupGuid,
  (state: RootState) => state.batchesAndBundlesTab.activeProductionGroupGuid,

  (batchesByProductionGroupGuid, activeProductionGroupGuid) =>
    activeProductionGroupGuid
      ? sortBy(batchesByProductionGroupGuid[activeProductionGroupGuid], (batch) => batch.id)
      : null,
);

export const getProductionComponentInstancesByBatchGuid = createSelector(
  (state: RootState) => state.batches,
  (state: RootState) => state.productionComponentInstances,

  (batches, productionComponentInstances) =>
    createMappingByKey(
      productionComponentInstances,
      (productionComponentInstance) => productionComponentInstance.batchGuid,
      batches,
    ),
);

export const getProductionComponentInstancesForActiveProductionGroupNotBatchedByAnalysisSetGuid = createSelector(
  getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab,

  (productionComponentInstancesForActiveProductionGroup) =>
    Object.values(productionComponentInstancesForActiveProductionGroup).reduce(
      (productionComponentInstancesByAnalysisSetGuid, productionComponentInstance) => {
        const { analysisSetGuid, batchGuid } = productionComponentInstance;
        if (batchGuid !== null) return productionComponentInstancesByAnalysisSetGuid;
        productionComponentInstancesByAnalysisSetGuid[analysisSetGuid] = (
          productionComponentInstancesByAnalysisSetGuid[analysisSetGuid] ?? []
        ).concat([productionComponentInstance]);
        return productionComponentInstancesByAnalysisSetGuid;
      },
      {} as { [analysisSetGuid: string]: readonly ProductionComponentInstance[] },
    ),
);

export const getQuantitiesForActiveProductionGroupByBatchGuidAndAnalysisSetGuid = createSelector(
  (state: RootState) => state.batches,
  getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab,

  (
    batches,
    productionComponentInstancesForActiveProductionGroup,
  ): { [batchGuid: string]: { [analysisSetGuid: string]: number } } => {
    const quantitiesByBatchGuid: { [batchGuid: string]: { [analysisSetGuid: string]: number } } = mapValues(
      batches,
      () => ({}) as { [analysisSetGuid: string]: number },
    );
    for (const productionComponentInstance of productionComponentInstancesForActiveProductionGroup) {
      const { analysisSetGuid, batchGuid } = productionComponentInstance;
      if (batchGuid === null) continue;
      if (!(batchGuid in quantitiesByBatchGuid)) quantitiesByBatchGuid[batchGuid] = {};
      if (!(analysisSetGuid in quantitiesByBatchGuid[batchGuid])) quantitiesByBatchGuid[batchGuid][analysisSetGuid] = 0;
      quantitiesByBatchGuid[batchGuid][analysisSetGuid] += 1;
    }
    return quantitiesByBatchGuid;
  },
);

export const getRoundedLinearFeetSumsByBatchGuid = createSelector(
  (state: RootState) => state.batches,
  getProductionComponentInstancesByBatchGuid,
  getLoadedTrussesByAnalysisSetGuid,

  (batches, productionComponentInstancesByBatchGuid, loadedTrussesByAnalysisSetGuid) =>
    Object.entries(productionComponentInstancesByBatchGuid).reduce(
      (linearFeetSumsByBatchGuid, [batchGuid, productionComponentInstances]) => {
        const analysisSetGuidsForBatch = uniq(
          productionComponentInstances.map(
            (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
          ),
        );
        // Don't calculate the linear feet for the batch until all the trusses for the batch are loaded.
        // This way we can avoid giving an inaccurate but plausible number if some of the trusses fail to load.
        if (analysisSetGuidsForBatch.every((analysisSetGuid) => analysisSetGuid in loadedTrussesByAnalysisSetGuid)) {
          let totalLinearInches = 0;
          for (const productionComponentInstance of productionComponentInstances) {
            const truss = loadedTrussesByAnalysisSetGuid[productionComponentInstance.analysisSetGuid];
            const width = WidthCache.get(productionComponentInstance.analysisSetGuid, truss);
            totalLinearInches += width * truss.numberOfPlies;
          }
          linearFeetSumsByBatchGuid[batchGuid] = Math.round(totalLinearInches / 12);
        }

        return linearFeetSumsByBatchGuid;
      },

      // Initialize the value for all batches to zero so that those that do not have production component instances will
      // still have a value in the resulting object.
      mapValues(batches, () => 0),
    ),
);
